import React, { useState } from 'react';
import { FaUpload, FaTimes, FaDownload, FaEye } from 'react-icons/fa';
import { v4 as uuidv4 } from 'uuid';
import styles from '../CSS/FileAttachmentHandler.module.css';

// Componente para gestionar archivos adjuntos
const FileAttachmentHandler = {
  // Guardar archivo localmente
  uploadFiles: async (files) => {
    return new Promise((resolve, reject) => {
      try {
        const MAX_SIZE = 20 * 1024 * 1024; // 20MB en bytes
        const validFiles = [];
        const invalidFiles = [];
        
        for (const file of files) {
          if (file.size <= MAX_SIZE) {
            validFiles.push(file);
          } else {
            invalidFiles.push(file);
          }
        }
        
        if (invalidFiles.length > 0) {
          alert(`${invalidFiles.length} archivo(s) exceden el límite de 20MB y no se adjuntarán.`);
        }
        
        // Procesar archivos válidos
        const uploadedFiles = [];
        let processedCount = 0;
        
        validFiles.forEach(file => {
          const reader = new FileReader();
          
          reader.onload = (event) => {
            // Generar nombre único para el archivo
            const filename = `${Date.now()}_${file.name}`;
            
            // En producción, guardaríamos en src/files
            // Por ahora simulamos con localStorage
            const fileData = {
              id: uuidv4(),
              name: file.name,
              size: file.size,
              type: file.type,
              localUrl: `src/files/${filename}`,
              content: event.target.result,
              uploadedAt: new Date().toISOString()
            };
            
            // Guardar en localStorage (simula guardado en directorio)
            const filesStr = localStorage.getItem('ticket_files');
            let files = filesStr ? JSON.parse(filesStr) : [];
            files.push(fileData);
            localStorage.setItem('ticket_files', JSON.stringify(files));
            
            // En un entorno real crearíamos una URL para acceder al archivo
            const fileUrl = URL.createObjectURL(file);
            
            uploadedFiles.push({
              id: fileData.id,
              name: file.name,
              type: file.type,
              size: file.size,
              url: fileUrl, // Url temporal para visualización
              localUrl: fileData.localUrl // Referencia para recuperar después
            });
            
            processedCount++;
            if (processedCount === validFiles.length) {
              resolve(uploadedFiles);
            }
          };
          
          reader.onerror = () => {
            processedCount++;
            if (processedCount === validFiles.length) {
              resolve(uploadedFiles);
            }
          };
          
          reader.readAsDataURL(file);
        });
        
        if (validFiles.length === 0) {
          resolve([]);
        }
      } catch (error) {
        console.error('Error uploading files:', error);
        reject(error);
      }
    });
  },
  
  // Recuperar un archivo del almacenamiento local
  getFile: (fileId) => {
    try {
      const filesStr = localStorage.getItem('ticket_files');
      if (!filesStr) return null;
      
      const files = JSON.parse(filesStr);
      const file = files.find(f => f.id === fileId);
      
      if (!file) return null;
      
      return {
        ...file,
        url: file.content // En un sistema real, esto sería una URL a la ubicación del archivo
      };
    } catch (error) {
      console.error('Error retrieving file:', error);
      return null;
    }
  },
  
  // Formatear tamaño de archivo
  formatFileSize: (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    else return (bytes / 1048576).toFixed(1) + ' MB';
  },
  
  // Componente para previsualizar archivos
  FilePreviewModal: ({ file, onClose }) => {
    if (!file) return null;
    
    // Renderizado de vista previa de archivo
    const renderFilePreview = () => {
      if (file.type.startsWith('image/')) {
        return (
          <img 
            src={file.url} 
            alt={file.name} 
            className={styles.filePreviewImage}
          />
        );
      } else {
        let icon = '📎'; // Default icon
        
        // Select icon based on file type
        if (file.type.includes('pdf')) {
          icon = '📄';
        } else if (file.type.includes('word') || file.type.includes('document')) {
          icon = '📝';
        } else if (file.type.includes('sheet') || file.type.includes('excel')) {
          icon = '📊';
        } else if (file.type.includes('zip') || file.type.includes('compressed')) {
          icon = '🗜️';
        } else if (file.type.includes('video')) {
          icon = '🎬';
        } else if (file.type.includes('audio')) {
          icon = '🎵';
        } else if (file.type.includes('text')) {
          icon = '📃';
        }
        
        return (
          <div className={styles.filePreviewIcon}>
            {icon}
            <p>Vista previa no disponible para {file.name}</p>
          </div>
        );
      }
    };
    
    return (
      <div 
        className={styles.filePreviewOverlay}
        onClick={onClose}
      >
        <div 
          className={styles.filePreviewContainer}
          onClick={e => e.stopPropagation()}
        >
          <div className={styles.filePreviewHeader}>
            <h4>{file.name}</h4>
            <button 
              className={styles.filePreviewClose}
              onClick={onClose}
            >
              <FaTimes />
            </button>
          </div>
          <div className={styles.filePreviewContent}>
            {renderFilePreview()}
          </div>
          <div className={styles.filePreviewFooter}>
            <a 
              href={file.url} 
              download={file.name}
              className={styles.filePreviewDownload}
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaDownload className={styles.filePreviewDownloadIcon} />
              Descargar archivo
            </a>
          </div>
        </div>
      </div>
    );
  },
  
  // Componente para visualizar archivos adjuntos
  AttachmentViewer: ({ attachments, isMessageAttachment = false, onPreviewFile }) => {
    if (!attachments || !attachments.length) return null;
    
    // Formatear tamaño de archivo
    const formatFileSize = (bytes) => {
      if (bytes < 1024) return bytes + ' B';
      else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
      else return (bytes / 1048576).toFixed(1) + ' MB';
    };
    
    return (
      <div className={isMessageAttachment ? styles.messageAttachments : styles.attachmentsContainer}>
        <div className={isMessageAttachment ? styles.attachmentsHeader : styles.attachmentsTitle}>
          <span>Archivos adjuntos ({attachments.length})</span>
        </div>
        <div className={isMessageAttachment ? styles.messageAttachmentList : styles.attachmentsList}>
          {attachments.map((adjunto, idx) => (
            <div key={idx} className={isMessageAttachment ? styles.messageAttachmentItem : styles.attachmentItem}>
              <div 
                className={isMessageAttachment ? styles.messageAttachmentIcon : styles.attachmentIcon}
                onClick={() => onPreviewFile(adjunto)}
              >
                {adjunto.tipo?.startsWith('image/') ? (
                  <img 
                    src={adjunto.url} 
                    alt={adjunto.nombre}
                    className={isMessageAttachment ? styles.messageAttachmentThumb : styles.attachmentThumbnail}
                  />
                ) : (
                  <div className={isMessageAttachment ? styles.messageAttachmentFileIcon : styles.attachmentFileIcon}>
                    {adjunto.tipo?.includes('pdf') ? '📄' : 
                     adjunto.tipo?.includes('word') ? '📝' : 
                     adjunto.tipo?.includes('excel') ? '📊' : 
                     adjunto.tipo?.includes('zip') ? '🗜️' : '📎'}
                  </div>
                )}
              </div>
              <div className={isMessageAttachment ? styles.messageAttachmentInfo : styles.attachmentDetails}>
                <div className={isMessageAttachment ? styles.messageAttachmentName : styles.attachmentName}>
                  {adjunto.nombre}
                </div>
                <div className={isMessageAttachment ? styles.messageAttachmentSize : styles.attachmentSize}>
                  {formatFileSize(adjunto.tamaño || 0)}
                </div>
              </div>
              <div className={styles.attachmentActions}>
                <button 
                  className={styles.attachmentPreview}
                  onClick={() => onPreviewFile(adjunto)}
                  title="Ver archivo"
                >
                  <FaEye />
                </button>
                <a 
                  href={adjunto.url} 
                  download={adjunto.nombre}
                  className={styles.attachmentDownload}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Descargar archivo"
                >
                  <FaDownload />
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  },
  
  // Componente para selector de archivos
  FileUploader: ({ onFilesSelected }) => {
    const [dragging, setDragging] = useState(false);
    
    const handleDragOver = (e) => {
      e.preventDefault();
      setDragging(true);
    };
    
    const handleDragLeave = () => {
      setDragging(false);
    };
    
    const handleDrop = (e) => {
      e.preventDefault();
      setDragging(false);
      
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        onFilesSelected(e.dataTransfer.files);
      }
    };
    
    const handleFileChange = (e) => {
      if (e.target.files && e.target.files.length > 0) {
        onFilesSelected(e.target.files);
      }
    };
    
    return (
      <div 
        className={`${styles.fileUploader} ${dragging ? styles.dragging : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className={styles.fileUploaderContent}>
          <FaUpload className={styles.uploadIcon} />
          <p>Arrastra archivos aquí o <label className={styles.fileInputLabel}>
            busca en tu dispositivo
            <input 
              type="file" 
              multiple 
              onChange={handleFileChange}
              className={styles.fileInput}
            />
          </label></p>
          <p className={styles.fileHint}>Tamaño máximo: 20MB por archivo</p>
        </div>
      </div>
    );
  }
};

export default FileAttachmentHandler;